import { QueryColumn } from "query/QueryColumn";
import { Cell, CellType } from "table/Cell";
import { Row } from "table/Row";
import { ExpanderIterator } from "./ExpanderIterator";
import { SingleValueExpanderIterator } from "./SingleValueExpanderIterator";

export class CollectionExpanderIterator extends ExpanderIterator {
    private _childs: ExpanderIterator[] = [];
    private _source: Row[]; // 
    private _sourceIndex: number = 0;
    private _allChildrenExhausted: boolean = true; // Will be repopulated on first iteration.

    constructor(column: QueryColumn, columnNumber: number) {
        super(column, columnNumber);
        this._source = []; // Hush the compiler

        for (let i = 0; i < column.children().length; i++) {
            let c: QueryColumn = column.children()[i];
            let newChild: ExpanderIterator;
            if (c.canHaveChildren()) {
                newChild = new CollectionExpanderIterator(c, i);
            } else {
                newChild = new SingleValueExpanderIterator(c, i);
            }
            this._childs.push(newChild);
        }
    }

    public nextInto = (accumulator: Row) => {
        if (this._column.isExpanded) {
            if (this.handleEmptySource(accumulator)) {
                return;
            }
            this.populateChildren();

            let allExhausted = true;
            for (let each of this._childs) {
                each.nextInto(accumulator);
                if (!each.isExhausted()) {
                    allExhausted = false;
                }
            }
            this._allChildrenExhausted = allExhausted;
            if (allExhausted && this._sourceIndex >= this._source.length) {
                this._isExhausted = true;
            }
    
        } else {
            this.pushCollectionSummary(accumulator);
        };
    }

    private handleEmptySource = (accumulator : Row) => {
        if (this._source.length > 0) {
            return false;
        }

        for (let i=0; i<this._childs.length; i++) {
            let c = new Cell();
            c._type = CellType.EXHAUSTED;
            c._value = "";
            accumulator.addCell(c);
        }        

        return true;
    }

    private pushCollectionSummary = (accumulator: Row) => {
        let c = new Cell();
        c._type = CellType.SINGLE;
        c._value = ""; // TODO: eventually we'll have sum, average, count, etc.
        accumulator.addCell(c);
        this._isExhausted = true;
    }

    /* Make sure that any missing or expired children are replenished. */
    private populateChildren = () => {

        if (!this._column.isExpanded || !this._source) {
            return;
        }

        if (!this._allChildrenExhausted) {
            // Repopulation not needed.
            return;
        }

        let childColumns = this._column.children();
        let row = this._source[this._sourceIndex];
        for (let i = 0; i < childColumns.length; i++) {
            let child = this._childs[i];
            if (undefined !== row && undefined !== row.cells[i]) {
                child.replenishWith(row.cells[i]);
            } else {
                child.noDataAvailable();
            }
        }

        this._sourceIndex = this._sourceIndex + 1;;
    }

    public isExhausted = () => {
        return this._isExhausted;
    }

    public replenishWith = (source: Cell) => {
        if (CellType.SINGLE == source._type) {
            throw new Error("CollectionExpanderIterators must be made with collection cells.");
        }

        if (!source) {
            throw new Error("CollectionExpanderIterators requires a source.");
        }

        if (!Array.isArray(source._value)) {
            throw new Error("CollectionExpanderIterators need to be replenished with a Row[].");
        }

        this._source = source._value as Row[];
        this._sourceIndex = 0;
        this._isExhausted = false;
        this._allChildrenExhausted = true; // Make all children repopulate on first iteration.
    }

    /** If data was missing from the server's response, this is invoked instead of replenishWith. For unexpanded collections, this is normal. */
    public noDataAvailable = () => {
        let filler: Cell = new Cell();
        filler._type = CellType.EXHAUSTED;
        filler._value = [];
        this._isExhausted = true;
    }

}